// Relatório PDF de Estoque v2 — Entradas (NF/fornecedor/conferência) e Saídas (romaneios + antigos)
// Mantém o padrão visual OBRASUL (pdfKit): agrupado por dia, subtotais, resumos e rodapé interno.
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { pdfKit } from "@/lib/financialReportPDF";

const { NAVY, GOLD, TEXT, MUTED, BORDER, PAGE_W, M, fmtBRL, drawHeader, drawCards, drawFooters, TABLE_STYLES } = pdfKit;
const GROUP_BG = [228, 232, 242];
const SUBTOTAL_BG = [245, 246, 250];
const fmtQtd = (q) => (q === null || q === undefined ? "—" : Number(q).toLocaleString("pt-BR", { maximumFractionDigits: 2 }));
const fmtD = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "—");

function agruparPorDia(itens, dataKey) {
  const dias = {};
  itens.forEach((i) => { (dias[i[dataKey]] = dias[i[dataKey]] || []).push(i); });
  return Object.keys(dias).sort().map((d) => ({ dia: d, itens: dias[d] }));
}

function tabelaSecao(doc, y, titulo, head, body, columnStyles, extra = {}) {
  if (y > 175) { doc.addPage(); y = 14; }
  doc.setFont("helvetica", "bold").setFontSize(8.5).setTextColor(...NAVY);
  doc.text(titulo, M, y);
  autoTable(doc, { startY: y + 2, margin: { left: M, right: M }, head: [head], body,
    ...TABLE_STYLES, columnStyles, ...extra });
  return doc.lastAutoTable.finalY + 7;
}

export async function gerarRelatorioEstoquePDF({ tipo, dados, dataInicio, dataFim, filtroLabel }) {
  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "landscape", compress: true });
  const periodo = `${fmtD(dataInicio)} a ${fmtD(dataFim)}`;
  const itens = dados.itens || [];
  let y = drawHeader(doc, {
    docLabel: "Relatório de Estoque",
    tituloParts: [
      { text: "Relatório de", color: NAVY },
      { text: tipo === "entrada" ? "Entradas de Estoque" : "Saídas de Estoque", color: GOLD },
    ],
    subtitle: tipo === "saida" ? "Fonte: romaneios despachados + lançamentos antigos (marcados)." : "Compras conferidas NF × pedido.",
    filtros: [
      { label: "Período", value: periodo },
      { label: "Obra", value: filtroLabel || "Todas as obras" },
    ],
    dataEmissao: new Date().toLocaleString("pt-BR"),
  });

  const cards = tipo === "entrada"
    ? [{ label: "Entradas", value: String(dados.total || 0) },
       { label: "Sem NF", value: String(dados.sem_nf || 0), color: dados.sem_nf ? [185, 28, 28] : undefined },
       { label: "Valor total", value: fmtBRL(dados.valor_total || 0) }]
    : [{ label: "Lançamentos", value: String(dados.total || 0) },
       { label: "Possíveis duplicidades", value: String(dados.duplicidades || 0), color: dados.duplicidades ? [185, 28, 28] : undefined },
       { label: "Valor total", value: fmtBRL(dados.valor_total || 0) }];
  y = drawCards(doc, y, cards);

  // ---- tabela principal agrupada por dia ----
  const head = tipo === "entrada"
    ? ["Data", "Código", "Material", "Qtd", "Unid.", "Valor Unit.", "Valor Total", "NF", "Fornecedor", "Lançado por", "Conf. NF"]
    : ["Data", "Romaneio Nº", "Código", "Material", "Qtd env.", "Qtd rec.", "Unid.", "Valor Unit.", "Valor Total", "Obra / Centro de custo", "Recebido por", "Conf."];
  const body = [];
  agruparPorDia(itens, "data").forEach(({ dia, itens: lst }) => {
    body.push([{ content: fmtD(dia), colSpan: head.length, styles: { fillColor: GROUP_BG, fontStyle: "bold", textColor: NAVY } }]);
    let sub = 0;
    lst.forEach((i) => {
      sub += i.estornado ? 0 : (i.valor_total || 0);
      if (tipo === "entrada") {
        body.push([fmtD(i.data), i.codigo || "", i.material || "", fmtQtd(i.quantidade), i.unidade || "",
          fmtBRL(i.valor_unitario || 0), fmtBRL(i.valor_total || 0),
          i.sem_nf ? "SEM NF" : (i.nota_fiscal || ""), i.fornecedor || "—", i.lancado_por || "—",
          i.conferido_nf ? "X" : ""].map((c, idx) => (i.sem_nf && idx === 7 ? { content: c, styles: { textColor: [185, 28, 28], fontStyle: "bold" } } : c)));
      } else {
        const marcas = [i.possivel_duplicidade ? "DUP?" : null, i.estornado ? "ESTORNADO" : null].filter(Boolean).join(" ");
        body.push([fmtD(i.data), i.romaneio_numero || "antigo", i.codigo || "",
          `${i.material || ""}${marcas ? ` [${marcas}]` : ""}`,
          fmtQtd(i.qtd_enviada), fmtQtd(i.qtd_recebida), i.unidade || "",
          fmtBRL(i.valor_unitario || 0), fmtBRL(i.valor_total || 0), i.obra || "—",
          i.recebido_por || "—", i.conf || "—"].map((c) =>
          (i.possivel_duplicidade ? { content: c, styles: { fillColor: [253, 240, 215] } }
            : i.antigo ? { content: c, styles: { textColor: [120, 128, 140] } } : c)));
      }
    });
    body.push([{ content: `Subtotal do dia ${fmtD(dia)}`, colSpan: head.length - 1, styles: { fillColor: SUBTOTAL_BG, fontStyle: "bold", halign: "right" } },
               { content: fmtBRL(sub), styles: { fillColor: SUBTOTAL_BG, fontStyle: "bold" } }]);
  });
  autoTable(doc, { startY: y, margin: { left: M, right: M }, head: [head], body, ...TABLE_STYLES });
  y = doc.lastAutoTable.finalY + 7;

  // ---- resumo por material ----
  const porMat = {};
  itens.forEach((i) => {
    if (i.estornado) return;
    const k = i.codigo || i.material;
    const p = porMat[k] = porMat[k] || { material: i.material, codigo: i.codigo, qtd: 0, valor: 0 };
    p.qtd += Number((tipo === "entrada" ? i.quantidade : (i.qtd_recebida ?? i.qtd_enviada)) || 0);
    p.valor += i.valor_total || 0;
  });
  y = tabelaSecao(doc, y, "RESUMO POR MATERIAL", ["Código", "Material", "Qtd", "Valor total"],
    Object.values(porMat).sort((a, b) => b.valor - a.valor).map((p) => [p.codigo || "", p.material, fmtQtd(p.qtd), fmtBRL(p.valor)]),
    { 0: { cellWidth: 25 }, 1: { cellWidth: 120 }, 2: { cellWidth: 30, halign: "right" }, 3: { cellWidth: 40, halign: "right" } });

  if (tipo === "saida") {
    // ---- resumo / subtotal por centro de custo ----
    const sub = dados.subtotais_centro || {};
    y = tabelaSecao(doc, y, "RESUMO POR OBRA / CENTRO DE CUSTO",
      ["Centro de custo", "Lançamentos", "Material (R$)"],
      Object.values(sub).map((s) => [s.titulo, String(s.itens), fmtBRL(s.total)]),
      { 0: { cellWidth: 150 }, 1: { cellWidth: 35, halign: "center" }, 2: { cellWidth: 45, halign: "right" } });

    // ---- resultado da obra (contrato + aditivos) ----
    if (dados.resultado) {
      const r = dados.resultado;
      const linhas = r.contratos.map((c) => [c.titulo, fmtBRL(c.valor)]);
      linhas.push([{ content: "Total contrato + aditivos", styles: { fontStyle: "bold" } }, { content: fmtBRL(r.total_contratos), styles: { fontStyle: "bold" } }]);
      linhas.push(["(−) Material recebido", fmtBRL(r.total_material)]);
      linhas.push([{ content: "RESULTADO DA OBRA (valor final)", styles: { fontStyle: "bold", textColor: NAVY } },
                   { content: fmtBRL(r.valor_final), styles: { fontStyle: "bold", textColor: r.valor_final >= 0 ? [21, 128, 61] : [185, 28, 28] } }]);
      y = tabelaSecao(doc, y, "RESULTADO DA OBRA (contrato + aditivos − material recebido)",
        ["Descrição", "Valor"], linhas, { 0: { cellWidth: 180 }, 1: { cellWidth: 50, halign: "right" } });
    }

    // ---- em apuracao (OF) ----
    if ((dados.em_apuracao || []).length) {
      y = tabelaSecao(doc, y, "EM APURAÇÃO (OF — faltantes aguardando resolução, fora do custo)",
        ["Romaneio", "Material", "Qtd", "Obra"],
        dados.em_apuracao.map((a) => [a.romaneio_numero, a.nome, fmtQtd(a.quantidade), a.obra_titulo || "—"]),
        { 0: { cellWidth: 30 }, 1: { cellWidth: 110 }, 2: { cellWidth: 25, halign: "center" }, 3: { cellWidth: "auto" } });
    }

    // ---- patrimonio em uso na obra ----
    if ((dados.patrimonio_obra || []).length) {
      y = tabelaSecao(doc, y, "PATRIMÔNIO EM USO NA OBRA (não soma no custo de material)",
        ["Código", "Item", "Categoria", "Desde", "Responsável", "Romaneio"],
        dados.patrimonio_obra.map((p) => [p.codigo || "", p.item, p.categoria || "—", fmtD(p.desde), p.responsavel || "—", p.romaneio || "—"]),
        { 0: { cellWidth: 28 }, 1: { cellWidth: 85 }, 2: { cellWidth: 45 }, 3: { cellWidth: 25 }, 4: { cellWidth: 50 }, 5: { cellWidth: "auto" } });
    }
  }

  drawFooters(doc);
  const filename = `Relatorio_Estoque_${tipo === "entrada" ? "Entradas" : "Saidas"}_${dataInicio}_${dataFim}.pdf`;
  return { blob: doc.output("blob"), filename };
}
